import React, { useState, useEffect, useMemo, useCallback } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  CircularProgress,
  Alert,
  IconButton,
  Button,
  Tooltip,
  TextField,
  InputAdornment,
  Select,
  MenuItem,
  FormControl,
  Chip,
  Grid,
  Card,
  CardContent,
  Divider,
  Tabs,
  Tab,
  Checkbox
} from '@mui/material';
import {
  Refresh as RefreshIcon,
  Search as SearchIcon,
  Clear as ClearIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  OpenInNew as OpenInNewIcon,
  Inventory as InventoryIcon,
  List as ListIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { useParams, Link } from 'react-router-dom';
import { installBaseService } from '../../services/installBase.service';
import { inventoryService } from '../../services/inventory.service';
import { format } from 'date-fns';
import InstallBaseBreadcrumbs from './InstallBaseBreadcrumbs';
import { apiService } from '../../services/api.service';
import { useFeatureFlags, FEATURE_CODES } from '../../context/FeatureFlagContext';
import PartOrderDialog from './dialogs/PartOrderDialog';

function TabPanel(props) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`location-tabpanel-${index}`}
      aria-labelledby={`location-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const LocationDetails = () => {
  const { locationId } = useParams();
  const [tabValue, setTabValue] = useState(0);
  const [locationData, setLocationData] = useState(null);
  const [installations, setInstallations] = useState([]);
  const [inventoryData, setInventoryData] = useState({});
  const [inventoryLoading, setInventoryLoading] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedItems, setSelectedItems] = useState([]);
  const { isFeatureEnabled } = useFeatureFlags();
  const canOrderParts = isFeatureEnabled(FEATURE_CODES.INSTALL_BASE_SPARE_PARTS);
  const [isOrderDialogOpen, setIsOrderDialogOpen] = useState(false);
  const [criticalLevelFilter, setCriticalLevelFilter] = useState('');
  const [stockStatusFilter, setStockStatusFilter] = useState('');

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch location details which now includes customer information
        const locationResponse = await apiService.get(`/install-base/locations/bylocationid/${locationId}`);
        console.log('Location Response:', locationResponse);

        if (!locationResponse) {
          throw new Error('No location details found');
        }

        // Set location data (which now includes customer_name)
        setLocationData(locationResponse);

        // Fetch installations and inventory data
        const [installationsResponse, inventoryResponse] = await Promise.all([
          installBaseService.getInstallationsByLocation(locationId),
          inventoryService.getInventoryByLocation(locationId)
        ]);

        // Handle installations response and fetch equipment model details
        if (Array.isArray(installationsResponse)) {
          // Fetch equipment model details for each installation
          const installationsWithModelDetails = await Promise.all(
            installationsResponse.map(async (installation) => {
              try {
                if (installation.model_id) {
                  const modelDetails = await installBaseService.getEquipmentModelById(installation.model_id);
                  return {
                    ...installation,
                    model_name: modelDetails.model_name,
                    manufacturer: modelDetails.manufacturer,
                    category: modelDetails.category,
                    service_life_years: modelDetails.service_life_years,
                    spec_detail: modelDetails.spec_detail
                  };
                }
                return installation;
              } catch (error) {
                console.error(`Error fetching model details for installation ${installation.installation_id}:`, error);
                return installation;
              }
            })
          );
          setInstallations(installationsWithModelDetails);
        } else {
          console.warn('No installations found or invalid response format');
          setInstallations([]);
        }

        // Handle inventory response
        if (inventoryResponse && inventoryResponse.inventory_data) {
          setInventoryData(inventoryResponse);
        } else {
          console.warn('No inventory data found or invalid response format');
          setInventoryData({
            totals: {
              total_quantity: 0,
              non_expired_quantity: 0,
              expired_quantity: 0,
              expiring_soon_quantity: 0
            },
            sku_count: 0,
            inventory_data: []
          });
        }

      } catch (err) {
        console.error('Error fetching location details:', err);
        setError(err.message || 'Failed to fetch location details');
      } finally {
        setLoading(false);
      }
    };

    if (locationId) {
      fetchData();
    }
  }, [locationId]);

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };

  const InstallationsList = () => {
    const filteredInstallations = installations.filter(installation => {
      const matchesSearch = installation.serial_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          installation.model_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
                          (installation.manufacturer && installation.manufacturer.toLowerCase().includes(searchTerm.toLowerCase())) ||
                          (installation.category && installation.category.toLowerCase().includes(searchTerm.toLowerCase()));
      const matchesStatus = !statusFilter || installation.status === statusFilter;
      return matchesSearch && matchesStatus;
    });

    return (
      <Box>
        <Box sx={{ mb: 3, display: 'flex', gap: 2, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Search installations..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
          />
          <FormControl size="small" sx={{ minWidth: 120 }}>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              displayEmpty
            >
              <MenuItem value="">All Status</MenuItem>
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="inactive">Inactive</MenuItem>
              <MenuItem value="maintenance">Maintenance</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <TableContainer component={Paper}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>Serial Number</TableCell>
                <TableCell>Model</TableCell>
                <TableCell>Manufacturer</TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Service Life</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Install Date</TableCell>
                <TableCell>Last Service</TableCell>
                <TableCell>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {filteredInstallations.map((installation) => (
                <TableRow key={installation.installation_id}>
                  <TableCell>{installation.serial_number}</TableCell>
                  <TableCell>{installation.model_name}</TableCell>
                  <TableCell>{installation.manufacturer}</TableCell>
                  <TableCell>{installation.category}</TableCell>
                  <TableCell>
                    {installation.service_life_years ? `${installation.service_life_years} years` : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={installation.status}
                      color={(() => {
                        const status = (installation.status || '').toLowerCase().trim();
                        if (status === 'active') return 'success';
                        if (status === 'repairing') return 'warning';
                        if (status === 'commissioning') return 'default';
                        return 'default';
                      })()}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    {installation.install_date ? format(new Date(installation.install_date), 'MMM d, yyyy') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    {installation.last_service_date ? format(new Date(installation.last_service_date), 'MMM d, yyyy') : 'N/A'}
                  </TableCell>
                  <TableCell>
                    <Box sx={{ display: 'flex', gap: 1 }}>
                      <Button
                        component={Link}
                        to={`/installations/${installation.installation_id}`}
                        startIcon={<OpenInNewIcon />}
                        size="small"
                      >
                        Details
                      </Button>
                      {installation.spec_detail && (
                        <Tooltip title="View Specifications">
                          <IconButton
                            size="small"
                            onClick={() => {
                              // You can implement a dialog to show specifications here
                              console.log('Specifications:', installation.spec_detail);
                            }}
                          >
                            <InfoIcon fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    );
  };

  const handleOrderParts = () => {
    setIsOrderDialogOpen(true);
  };

  const handleOrderSubmit = (orderData) => {
    // TODO: Implement order submission
    console.log('Order submitted:', orderData);
  };

  const InventoryDetails = () => {
    const defaultInventoryData = {
      totals: {
        total_quantity: 0,
        non_expired_quantity: 0,
        expired_quantity: 0,
        expiring_soon_quantity: 0
      },
      sku_count: 0,
      inventory_data: []
    };

    // Add utility functions at the top
    const getStockStatus = (available, target) => {
      if (available >= target) return 'In Stock';
      if (available > 0) return 'Partial Stock';
      return 'Out of Stock';
    };

    const getStockStatusColor = (status) => {
      switch (status) {
        case 'In Stock':
          return 'success';
        case 'Partial Stock':
          return 'warning';
        case 'Out of Stock':
          return 'error';
        default:
          return 'default';
      }
    };

    const getCriticalLevelColor = (level) => {
      switch (level) {
        case 'A': return 'error';
        case 'B': return 'warning';
        case 'C': return 'info';
        case 'D': return 'default';
        default: return 'default';
      }
    };

    // Add state for expanded categories and search
    const [expandedCategories, setExpandedCategories] = useState(() => {
      // Initialize with all categories expanded
      const initialExpanded = {};
      if (inventoryData?.inventory_data?.inventory_data) {
        inventoryData.inventory_data.inventory_data.forEach(item => {
          const category = item.category || 'Uncategorized';
          initialExpanded[category] = true;
        });
      }
      return initialExpanded;
    });

    // Ensure we have valid data with defaults and handle nested structure
    const safeInventoryData = {
      totals: {
        total_quantity: inventoryData?.inventory_data?.reduce((sum, item) => sum + item.total_quantity, 0) || 0,
        non_expired_quantity: inventoryData?.inventory_data?.reduce((sum, item) => sum + item.non_expired_quantity, 0) || 0,
        expired_quantity: inventoryData?.inventory_data?.reduce((sum, item) => sum + item.expired_quantity, 0) || 0,
        expiring_soon_quantity: inventoryData?.inventory_data?.reduce((sum, item) => sum + item.expiring_soon_quantity, 0) || 0
      },
      sku_count: inventoryData?.inventory_data?.length || 0,
      inventory_data: Array.isArray(inventoryData?.inventory_data) 
        ? inventoryData.inventory_data 
        : []
    };

    const { totals, sku_count, inventory_data } = safeInventoryData;

    // Group inventory items by category
    const groupedInventory = inventory_data.reduce((acc, item) => {
      const category = item.category || 'Uncategorized';
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(item);
      return acc;
    }, {});

    // Sort categories alphabetically
    const sortedCategories = Object.keys(groupedInventory).sort();

    // Get unique critical levels and stock statuses for filters
    const uniqueCriticalLevels = useMemo(() => {
      const levels = new Set(inventory_data.map(item => 
        item.stock_status?.critical_level?.value
      ).filter(Boolean));
      return Array.from(levels).sort();
    }, [inventory_data]);

    const uniqueStockStatuses = useMemo(() => {
      const statuses = new Set(inventory_data.map(item => 
        getStockStatus(item.total_quantity, item.target_quantity)
      ));
      return Array.from(statuses).sort();
    }, [inventory_data]);

    // Filter items based on search term and filters
    const filteredInventory = Object.entries(groupedInventory).reduce((acc, [category, items]) => {
      const filteredItems = items.filter(item => {
        const matchesSearch = item.sku.toLowerCase().includes(searchTerm.toLowerCase()) ||
                            item.description.toLowerCase().includes(searchTerm.toLowerCase());
        
        const matchesCriticalLevel = !criticalLevelFilter || 
          item.stock_status?.critical_level?.value === criticalLevelFilter;
        
        const stockStatus = getStockStatus(item.total_quantity, item.target_quantity);
        const matchesStockStatus = !stockStatusFilter || stockStatus === stockStatusFilter;

        return matchesSearch && matchesCriticalLevel && matchesStockStatus;
      });

      if (filteredItems.length > 0) {
        acc[category] = filteredItems;
      }
      return acc;
    }, {});

    // Get filtered categories
    const filteredCategories = Object.keys(filteredInventory).sort();

    // Update expanded state when new categories are added
    useEffect(() => {
      const newCategories = filteredCategories.filter(category => !(category in expandedCategories));
      if (newCategories.length > 0) {
        setExpandedCategories(prev => {
          const updated = { ...prev };
          newCategories.forEach(category => {
            // Only expand new categories if they contain items that match current filters
            if (filteredInventory[category]?.length > 0) {
              updated[category] = true;
            }
          });
          return updated;
        });
      }
    }, [filteredCategories, filteredInventory]);

    // Expand all categories
    const expandAll = useCallback(() => {
      const allExpanded = {};
      filteredCategories.forEach(category => {
        allExpanded[category] = true;
      });
      setExpandedCategories(allExpanded);
    }, [filteredCategories]);

    // Collapse all categories
    const collapseAll = useCallback(() => {
      setExpandedCategories({});
    }, []);

    const fetchInventoryData = useCallback(async (sku) => {
      if (inventoryLoading[sku]) return;
      
      setInventoryLoading(prev => ({ ...prev, [sku]: true }));
      
      try {
        const response = await inventoryService.getSupplierInventory(sku);
        
        if (response?.inventory_data) {
          const { inventory_data } = response.inventory_data;
          
          const transformedData = {
            total_available: response.inventory_data.total_available || 0,
            total_in_transit: response.inventory_data.total_in_transit || 0,
            total_ordered: response.inventory_data.total_ordered || 0,
            supplier_count: response.inventory_data.supplier_count || 0,
            inventory_data: inventory_data.map(supplier => ({
              supplier_name: supplier.supplier_name,
              available_quantity: supplier.available_quantity || 0,
              in_transit_quantity: supplier.in_transit_quantity || 0,
              ordered_quantity: supplier.ordered_quantity || 0
            }))
          };

          setInventoryData(prev => ({
            ...prev,
            [sku]: transformedData
          }));
        }
      } catch (error) {
        setInventoryData(prev => ({
          ...prev,
          [sku]: {
            error: true,
            errorMessage: error.message || 'Failed to fetch inventory data'
          }
        }));
      } finally {
        setInventoryLoading(prev => ({ ...prev, [sku]: false }));
      }
    }, []);

    // Toggle category expansion
    const toggleCategory = useCallback((category) => {
      setExpandedCategories(prev => ({
        ...prev,
        [category]: !prev[category]
      }));
    }, []);

    // Update handleItemSelect to store full item data
    const handleItemSelect = (item) => {
      setSelectedItems(prev => {
        if (prev.some(selected => selected.sku === item.sku)) {
          return prev.filter(selected => selected.sku !== item.sku);
        }
        return [...prev, item];
      });
    };

    return (
      <Box>
        <Typography variant="h6" gutterBottom>
          Inventory Summary
        </Typography>
        <Grid container spacing={2}>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Total SKUs
              </Typography>
              <Typography variant="h4">{sku_count}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Total Quantity
              </Typography>
              <Typography variant="h4">{totals.total_quantity}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Non-Expired
              </Typography>
              <Typography variant="h4">{totals.non_expired_quantity}</Typography>
            </Paper>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography variant="subtitle2" color="textSecondary">
                Expired/Expiring Soon
              </Typography>
              <Typography variant="h4">
                {totals.expired_quantity + totals.expiring_soon_quantity}
              </Typography>
            </Paper>
          </Grid>
        </Grid>

        <Box sx={{ mt: 4 }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6">
              Stock Status
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
              {selectedItems.length > 0 && canOrderParts && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleOrderParts}
                  startIcon={<InventoryIcon />}
                >
                  Order Parts ({selectedItems.length})
                </Button>
              )}
              <TextField
                size="small"
                placeholder="Search SKU or description..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
              />
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={criticalLevelFilter}
                  onChange={(e) => setCriticalLevelFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Critical Levels</MenuItem>
                  {uniqueCriticalLevels.map(level => (
                    <MenuItem key={level} value={level}>
                      Level {level}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <FormControl size="small" sx={{ minWidth: 120 }}>
                <Select
                  value={stockStatusFilter}
                  onChange={(e) => setStockStatusFilter(e.target.value)}
                  displayEmpty
                >
                  <MenuItem value="">All Stock Status</MenuItem>
                  {uniqueStockStatuses.map(status => (
                    <MenuItem key={status} value={status}>
                      {status}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <Button
                size="small"
                onClick={expandAll}
                variant="outlined"
                startIcon={<ExpandMoreIcon />}
              >
                Expand All
              </Button>
              <Button
                size="small"
                onClick={collapseAll}
                variant="outlined"
                startIcon={<ExpandLessIcon />}
              >
                Collapse All
              </Button>
            </Box>
          </Box>
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox"></TableCell>
                  <TableCell>SKU</TableCell>
                  <TableCell>Description</TableCell>
                  <TableCell>Category</TableCell>
                  <TableCell>Critical Level</TableCell>
                  <TableCell>Total Quantity</TableCell>
                  <TableCell>Target Quantity</TableCell>
                  <TableCell>Stock Status</TableCell>
                  <TableCell>Additional Inventory</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {inventory_data.length > 0 ? (
                  filteredCategories.map((category) => (
                    <React.Fragment key={category}>
                      <TableRow 
                        sx={{ 
                          backgroundColor: 'grey.100',
                          cursor: 'pointer',
                          '&:hover': {
                            backgroundColor: 'grey.200',
                          }
                        }}
                        onClick={() => toggleCategory(category)}
                      >
                        <TableCell padding="checkbox"></TableCell>
                        <TableCell colSpan={8}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {expandedCategories[category] ? (
                              <ExpandLessIcon fontSize="small" />
                            ) : (
                              <ExpandMoreIcon fontSize="small" />
                            )}
                            <Typography variant="subtitle1" sx={{ fontWeight: 'bold' }}>
                              {category}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              ({filteredInventory[category].length} items)
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                      {expandedCategories[category] && filteredInventory[category].map((item) => {
                        const stockStatus = getStockStatus(item.total_quantity, item.target_quantity);
                        const inventory = inventoryData[item.sku];
                        const isLoading = inventoryLoading[item.sku];
                        
                        // Parse critical level from stock_status
                        let criticalLevel = null;
                        try {
                          if (item.stock_status?.critical_level?.value) {
                            criticalLevel = item.stock_status.critical_level.value;
                          }
                        } catch (e) {
                          console.warn('Error parsing stock status:', e);
                        }

                        return (
                          <TableRow key={item.sku}>
                            <TableCell padding="checkbox">
                              <Checkbox
                                checked={selectedItems.some(selected => selected.sku === item.sku)}
                                onChange={(e) => {
                                  e.stopPropagation();
                                  handleItemSelect(item);
                                }}
                              />
                            </TableCell>
                            <TableCell>{item.sku}</TableCell>
                            <TableCell>{item.description}</TableCell>
                            <TableCell>{item.category}</TableCell>
                            <TableCell>
                              {criticalLevel ? (
                                <Chip
                                  label={`Level ${criticalLevel}`}
                                  color={getCriticalLevelColor(criticalLevel)}
                                  size="small"
                                  variant="outlined"
                                />
                              ) : (
                                '-'
                              )}
                            </TableCell>
                            <TableCell>{item.total_quantity}</TableCell>
                            <TableCell>{item.target_quantity || '-'}</TableCell>
                            <TableCell>
                              <Chip
                                label={stockStatus}
                                color={getStockStatusColor(stockStatus)}
                                size="small"
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>
                              {isLoading ? (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                  <CircularProgress size={20} />
                                  <Typography variant="caption">Loading...</Typography>
                                </Box>
                              ) : inventory ? (
                                inventory.error ? (
                                  <Tooltip title={inventory.errorMessage}>
                                    <Typography color="error">Error</Typography>
                                  </Tooltip>
                                ) : (
                                  <Tooltip 
                                    title={
                                      <Box sx={{ p: 1 }}>
                                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Inventory Details:</Typography>
                                        {inventory.inventory_data.map((supplier, index) => (
                                          <Box key={index} sx={{ mb: 0.5 }}>
                                            <Typography variant="body2" sx={{ fontWeight: 500 }}>
                                              {supplier.supplier_name}:
                                            </Typography>
                                            <Typography variant="body2" sx={{ ml: 1 }}>
                                              Available: {supplier.available_quantity}
                                              {supplier.in_transit_quantity > 0 && ` (${supplier.in_transit_quantity} in transit)`}
                                              {supplier.ordered_quantity > 0 && ` (${supplier.ordered_quantity} ordered)`}
                                            </Typography>
                                          </Box>
                                        ))}
                                      </Box>
                                    }
                                  >
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      <Typography>{inventory.total_available}</Typography>
                                      <IconButton
                                        size="small"
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          fetchInventoryData(item.sku);
                                        }}
                                      >
                                        <RefreshIcon fontSize="small" />
                                      </IconButton>
                                    </Box>
                                  </Tooltip>
                                )
                              ) : (
                                <Button
                                  variant="outlined"
                                  size="small"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    fetchInventoryData(item.sku);
                                  }}
                                  startIcon={<RefreshIcon />}
                                >
                                  Check Availability
                                </Button>
                              )}
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </React.Fragment>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No inventory data available
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      </Box>
    );
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <InstallBaseBreadcrumbs
        customerName={locationData?.customer_name}
        customerId={locationData?.customer_id}
        locationName={locationData?.name}
        locationId={locationId}
      />
      
      <Paper elevation={3}>
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <Typography variant="h6" gutterBottom>
                {locationData.name}
              </Typography>
              <Typography variant="body1" gutterBottom>
                {locationData.city}, {locationData.state}
              </Typography>
              <Typography variant="body1" gutterBottom>
                <strong>Type:</strong> {locationData.location_type}
              </Typography>
              {locationData.capacity && (
                <Typography variant="body1" gutterBottom>
                  <strong>Capacity:</strong> {locationData.capacity}
                </Typography>
              )}
              {locationData.site_note && (
                <Typography variant="body1" gutterBottom>
                  <strong>Site Notes:</strong> {locationData.site_note}
                </Typography>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <Typography variant="body1">
                <strong>Address:</strong> {locationData.address_line1 || 'N/A'}
                {locationData.address_line2 && <>, {locationData.address_line2}</>}
              </Typography>
              <Typography variant="body1">
                {locationData.city}, {locationData.state} {locationData.postal_code || ''}
              </Typography>
              <Typography variant="body1">
                <strong>Country:</strong> {locationData.country}
              </Typography>
              <Typography variant="body1">
                <strong>Location ID:</strong> {locationData.location_id}
              </Typography>
              <Typography variant="body1">
                <strong>Created:</strong> {format(new Date(locationData.created_at), 'MMM d, yyyy')}
              </Typography>
              <Typography variant="body1">
                <strong>Last Updated:</strong> {format(new Date(locationData.updated_at), 'MMM d, yyyy')}
              </Typography>
              <Typography variant="body1">
                <strong>Status:</strong> {locationData.is_active ? 'Active' : 'Inactive'}
              </Typography>
            </Grid>
          </Grid>
        </Box>

        <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
          <Tabs 
            value={tabValue} 
            onChange={handleTabChange}
            aria-label="location details tabs"
          >
            <Tab 
              icon={<ListIcon />} 
              label="Installations" 
              id="location-tab-0" 
              aria-controls="location-tabpanel-0" 
            />
            <Tab 
              icon={<InventoryIcon />} 
              label="Inventory" 
              id="location-tab-1" 
              aria-controls="location-tabpanel-1" 
            />
          </Tabs>
        </Box>

        <TabPanel value={tabValue} index={0}>
          <InstallationsList />
        </TabPanel>
        <TabPanel value={tabValue} index={1}>
          <InventoryDetails />
        </TabPanel>
      </Paper>

      <PartOrderDialog
        open={isOrderDialogOpen}
        onClose={() => setIsOrderDialogOpen(false)}
        selectedItems={selectedItems}
        locationData={locationData}
        onOrderSubmit={handleOrderSubmit}
      />
    </Box>
  );
};

export default LocationDetails; 